import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRouteLoaderData } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/util";
import { ROUTES } from "../../constants/routes";
import { HomeLoaderResponse } from "../../interfaces/loaders";
import { useCreateIdKortOrder } from "../../hooks/idkort/useCreateIdKortOrder";
import { stamdataQueryKeys } from "../../hooks/stamdata/stamdataQueryKeys";
import { useStamdataPerson } from "../../hooks/stamdata/useStamdataPerson";
import { Button } from "../../components/Button";
import FileInput from "../../components/form/FileInput";
import Spinner from "../../components/graphics/Spinner";
import Modal, { ModalRoot } from "../../components/Modal/Modal";
import ProfilIkkeKlarBesked from "../../components/ProfilIkkeKlarBesked";
import { erAfvistSomIkkeAdvokat } from "./erAfvistSomIkkeAdvokat";
import { getPersonligSideAfvisning } from "../../lib/personligSession";
import { getIdKortInitialValues } from "./getIdKortInitialValues";
import IkkePersonligSessionBesked from "../../components/IkkePersonligSessionBesked";
import { formatAdresseLines, harBrugbarAdresse } from "./formatAdresse";
import { getIdKortStatus, IdKortStatusBanner } from "./getIdKortStatus";
import { getIdKortSubmitFailure } from "./getIdKortSubmitFailure";
import PassportCropModal from "./PassportCropModal";

const isAllowedFotoSource = (file: File) =>
  /^image\/(jpe?g|png)$/i.test(file.type) || /\.(jpe?g|png)$/i.test(file.name);

const getSubmitErrorMessage = (error: unknown) => {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return "Der opstod en fejl. Prøv igen senere.";
};

const RequiredLabel = ({
  htmlFor,
  label,
}: {
  htmlFor: string;
  label: string;
}) => (
  <label className="form-label" htmlFor={htmlFor}>
    {label}
    <span className="text-danger" aria-hidden="true">
      {" *"}
    </span>
  </label>
);

const InfoField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="form-label mb-1 text-body">{label}</p>
    <div className="mb-0 text-muted">{value || "—"}</div>
  </div>
);

const StamdataInfoSection = ({ children }: { children: ReactNode }) => (
  <section
    aria-label="Oplysninger fra stamdata"
    className="rounded border border-secondary-subtle bg-light p-4 d-flex flex-column gap-3"
  >
    {children}
  </section>
);

// Klæber lige under portalens sticky navbar. Samme offset som formular-
// sidebaren bruger af samme grund (TESidebar: stickyTop="5rem"). z-index
// holdes under Bootstraps sticky-top (1020), så zonen glider ind under
// navbaren i stedet for at dække den.
const STATUS_ZONE_TOP = "5rem";
const STATUS_ZONE_Z_INDEX = 2;

// Den frist, medlemmet får at vide — ét sted, fordi den står både i kvitteringen og
// i statuszonen. Bekræftelsesmailen fra aika-backend lover det samme; de to skal
// rettes sammen, hvis fristen ændres.
const EKSPEDITIONSTID = "Der kan være op til 10 dages ekspeditionstid.";

// Kun fejlen kan lukkes — den er den eneste besked, medlemmet kan være færdig med
// at læse. "Under behandling" gælder, indtil kortet er sendt, og har derfor ingen
// luk-knap. role="alert": fejlen er presserende nok til at afbryde en skærmlæser,
// hvor statusbeskeden meldes høfligt.
const DismissibleAlert = ({
  dismissLabel,
  onDismiss,
  children,
}: {
  dismissLabel: string;
  onDismiss: () => void;
  children: ReactNode;
}) => (
  <div
    className="alert alert-danger d-flex align-items-start justify-content-between gap-3 mb-0"
    role="alert"
  >
    <span>{children}</span>
    <button
      type="button"
      className="btn-close"
      aria-label={dismissLabel}
      onClick={onDismiss}
    />
  </div>
);

/**
 * Statuszonen: præcis én besked, klæbende i toppen af formularen.
 *
 * ADV-186 flyttede kvitteringen ned til Send-knappen, fordi den stod langt over
 * folden. Kunden vil have den i toppen igen (ADV-204), og sticky-placeringen
 * opfylder begge hensyn: øverst i dokumentet, altid inden for synsfeltet.
 *
 * Kvitteringen selv bor ikke længere her, men i en pop-up (ADV-233) — zonen
 * viser det blivende: at anmodningen er under behandling.
 *
 * Ligger bevidst uden for formularens fieldset — et disabled fieldset ville
 * også deaktivere luk-knappen, når formularen låses efter en bestilling.
 */
const StatusZone = ({
  banner,
  submitError,
  onDismissError,
}: {
  banner: IdKortStatusBanner;
  submitError: string | null;
  onDismissError: () => void;
}) => {
  if (banner === "none") return null;

  return (
    <div
      className="position-sticky"
      style={{ top: STATUS_ZONE_TOP, zIndex: STATUS_ZONE_Z_INDEX }}
    >
      {banner === "error" ? (
        <DismissibleAlert
          dismissLabel="Luk fejlmeddelelse"
          onDismiss={onDismissError}
        >
          {submitError}
        </DismissibleAlert>
      ) : null}

      {banner === "inProgress" ? (
        <div className="alert alert-warning mb-0" role="status">
          Vi er ved at behandle din anmodning om ID-kort. {EKSPEDITIONSTID}
        </div>
      ) : null}
    </div>
  );
};

const IdKortForm = () => {
  const queryClient = useQueryClient();
  const { userInfo } = useRouteLoaderData(
    ROUTES.homeIndex.id,
  ) as HomeLoaderResponse;

  // Aldrig fra loader-data — useStamdataPerson ejer den beslutning nu.
  const { stamdata, profilStatus } = useStamdataPerson();

  const prefilledValues = useMemo(
    () => getIdKortInitialValues({ userInfo, stamdata }),
    [userInfo, stamdata],
  );

  const adresseLinjer = useMemo(
    () => formatAdresseLines(stamdata?.adresse),
    [stamdata?.adresse],
  );
  const hasUsableAdresse = harBrugbarAdresse(stamdata?.adresse);

  const createMutation = useCreateIdKortOrder();
  const [foto, setFoto] = useState<File | null>(null);
  const [bemaerkninger, setBemaerkninger] = useState("");
  const [formKey, setFormKey] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedInThisSession, setSubmittedInThisSession] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Kvitterings-pop-uppen. Egen tilstand og ikke afledt af submittedInThisSession:
  // den lukkes, mens bestillingen bliver ved med at være sket — statuszonen skal
  // stå med den gule besked bagefter.
  const [kvitteringOpen, setKvitteringOpen] = useState(false);
  // Sat når serveren har afvist en bestilling med 409. Serveren har dermed
  // fortalt os, at der ligger en anmodning — den viden må ikke afhænge af, om
  // den efterfølgende stamdata-genhentning lykkes, ellers ender et afvist
  // forsøg i tavshed.
  const [blockedByExistingOrder, setBlockedByExistingOrder] = useState(false);

  // Foto-flow: kildefil -> beskæringsmodal -> beskåret pasfoto i foto
  const [cropSource, setCropSource] = useState<File | null>(null);
  const [fotoError, setFotoError] = useState<string | null>(null);
  const [fotoPreviewUrl, setFotoPreviewUrl] = useState<string | null>(null);

  // Bekræftelses-gate i modalen
  const [adresseBekraeftet, setAdresseBekraeftet] = useState(false);

  // Preview-URL for det beskårne foto (ryddes op når fotoet ændres).
  useEffect(() => {
    if (!foto) {
      // TODO(react-hooks-7): only the clearing branch is a synchronous setState;
      // the createObjectURL branch below is a legitimate external-resource sync.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFotoPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(foto);
    setFotoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [foto]);

  // Fra den friske forespørgsel, aldrig fra loaderStamdata (se query-options
  // ovenfor). To kilder til samme sandhed — stamdata og serverens 409 — OR'es
  // her, så en fejlet genhentning ikke kan skjule en anmodning, serveren
  // allerede har bekræftet findes.
  const harAktivIdKortAnmodning =
    (stamdata?.harAktivIdKortAnmodning ?? false) || blockedByExistingOrder;

  const status = getIdKortStatus({
    harAktivIdKortAnmodning,
    submittedInThisSession,
    submitError,
  });

  // Foto vælges -> valider format -> åbn beskæringsmodal. Selve fotoet sættes
  // først når brugeren bekræfter beskæringen (obligatorisk pasfoto-format).
  const handleFotoSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    // Nulstil input, så samme fil kan vælges igen.
    event.target.value = "";
    if (!file) return;
    if (!isAllowedFotoSource(file)) {
      setFotoError("Fotoet skal være i JPG-, JPEG- eller PNG-format.");
      return;
    }
    setFotoError(null);
    setCropSource(file);
  };

  const handleCropConfirm = (cropped: File) => {
    setFoto(cropped);
    setCropSource(null);
    setFotoError(null);
  };

  const resetConfirmGate = () => {
    setAdresseBekraeftet(false);
  };

  // Bevidst fejl-tolerant. Statuszonen og låsen hviler på submittedInThisSession
  // og blockedByExistingOrder netop for at overleve en fejlet genhentning — så et
  // afvist kald her må hverken vælte indsendelsen eller nå catch-blokken nedenfor
  // og blive vist som en rød fejl på en bestilling, serveren har taget imod.
  const refreshStamdata = async () => {
    if (!userInfo?.id) return;
    try {
      await queryClient.invalidateQueries({
        queryKey: stamdataQueryKeys.person(userInfo.id),
      });
    } catch {
      // Med vilje tavs: se ovenfor.
    }
  };

  const submitOrder = async () => {
    if (
      !foto ||
      !prefilledValues.fuldeNavn.trim() ||
      !prefilledValues.firmanavn.trim() ||
      !hasUsableAdresse ||
      !adresseBekraeftet
    ) {
      return;
    }

    setSubmitError(null);
    setSubmittedInThisSession(false);
    setBlockedByExistingOrder(false);

    try {
      await createMutation.mutateAsync({
        foto,
        bemaerkninger: bemaerkninger.trim() || undefined,
      });
      setFoto(null);
      setBemaerkninger("");
      setFormKey((current) => current + 1);
      setSubmittedInThisSession(true);
      setConfirmOpen(false);
      resetConfirmGate();
      // Først her, hvor serveren har taget imod bestillingen. En kvittering ved
      // klikket på Send ville love noget, vi endnu ikke ved holder.
      setKvitteringOpen(true);

      await refreshStamdata();
    } catch (error) {
      setConfirmOpen(false);
      resetConfirmGate();

      // Serveren bruger 409 til to ting, og de må ikke vises ens — se
      // getIdKortSubmitFailure. Dobbeltbestillings-spærringen er ikke medlemmets
      // fejl, så statuszonen falder ud i "under behandling" frem for en rød fejl.
      // Flaget sættes før genhentningen, så beskeden også står der, hvis
      // genhentningen fejler. En manglende Person-række (ADV-274) betyder
      // derimod, at intet blev gemt, og skal vises som fejl med serverens besked.
      if (getIdKortSubmitFailure(error) === "existingOrder") {
        setBlockedByExistingOrder(true);
        await refreshStamdata();
        return;
      }

      setSubmitError(getSubmitErrorMessage(error));
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      !foto ||
      !prefilledValues.fuldeNavn.trim() ||
      !prefilledValues.firmanavn.trim() ||
      status.formLocked
    ) {
      return;
    }

    resetConfirmGate();
    setConfirmOpen(true);
  };

  const isSubmitting = createMutation.isPending;
  const hasRequiredStamdata =
    Boolean(prefilledValues.fuldeNavn.trim()) &&
    Boolean(prefilledValues.firmanavn.trim());

  const canConfirmSend = !isSubmitting && hasUsableAdresse && adresseBekraeftet;

  // ADV-356: rækkefølgen mellem de to afvisninger herunder afgøres og testes i
  // getPersonligSideAfvisning. En firma- eller revisor-session har intet person-id, så dens profil
  // findes aldrig, og profil-beskeden sendte medlemmet efter en fejl, der ikke var der.
  const afvisning = getPersonligSideAfvisning({ userInfo, profilStatus });

  if (afvisning === "ikkePersonligSession") {
    return (
      <IkkePersonligSessionBesked
        identityType={userInfo.identityType}
        navn={userInfo.navn}
        konsekvens="ID-kort er personlige og kan kun bestilles"
      />
    );
  }

  // Uden en profil er formularen en blindgyde: Fulde navn falder tilbage på tokenets navn, mens
  // firmanavn og leveringsadresse er tomme — så Send er permanent inaktiv (hasRequiredStamdata),
  // uden at noget forklarer hvorfor. Bestillingen ville i øvrigt blive afvist af serveren, fordi
  // ID-kortet ikke kan skrives på en person, der ikke findes (409, ADV-274).
  if (afvisning === "profilIkkeKlar") {
    return (
      <ProfilIkkeKlarBesked konsekvens="du kan ikke bestille et ID-kort endnu" />
    );
  }

  // ADV-324: kun udøvende advokater må bestille. Formularen vises slet ikke — en fuldmægtig skal
  // ikke uploade og beskære et pasfoto for at få et afslag. Teksten er den samme, som serverens
  // 403 bærer, så medlemmet får samme svar, uanset hvilken af de to der siger det.
  if (erAfvistSomIkkeAdvokat(stamdata)) {
    return (
      <div className="alert alert-info mb-0" role="status">
        Det er kun advokater, der kan bestille ID-kort, og du er ikke
        registreret som advokat hos Advokatsamfundet. Kontakt Advokatsamfundet,
        hvis det ikke er rigtigt.
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="d-flex flex-column gap-4">
        <StatusZone
          banner={status.banner}
          submitError={submitError}
          onDismissError={() => setSubmitError(null)}
        />

        <fieldset disabled={isSubmitting || status.formLocked}>
          <div key={formKey} className="d-flex flex-column gap-4">
            <StamdataInfoSection>
              <InfoField label="Fulde navn" value={prefilledValues.fuldeNavn} />
              <InfoField label="Firmanavn" value={prefilledValues.firmanavn} />
            </StamdataInfoSection>

            <div>
              <RequiredLabel htmlFor="idkort-foto" label="Upload foto" />

              {/* Står FØR filvælgeren med vilje (ADV-245): kravene gælder motivet
                  — ansigt, baggrund, ingen solbriller — og dem kan beskæringen
                  bagefter ikke rette op på. Kravene er politiets, så vi linker til
                  kilden i stedet for at parafrasere dem. */}
              <p className="form-text mt-0 mb-2">
                Billedet skal opfylde de samme krav som et pasfoto — se{" "}
                <a
                  className="link"
                  target="_blank"
                  rel="noreferrer"
                  href="https://politi.dk/pas/krav-til-pas-og-koerekortfoto"
                >
                  politiets krav til pasfoto
                </a>
                .
              </p>

              <FileInput
                id="idkort-foto"
                accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                onChange={handleFotoSelected}
                fileName={foto?.name}
                aria-describedby="idkort-foto-hjaelp"
              />
              <div id="idkort-foto-hjaelp" className="form-text">
                Fotoet skal være png eller jpg. Du beskærer selv fotoet til
                pasfoto-format.
              </div>
              {fotoError ? (
                <div className="text-danger small mt-1">{fotoError}</div>
              ) : null}
              {fotoPreviewUrl ? (
                <div className="d-flex align-items-center gap-3 mt-3">
                  <img
                    src={fotoPreviewUrl}
                    alt="Beskåret pasfoto"
                    style={{
                      width: 70,
                      height: 90,
                      objectFit: "cover",
                      borderRadius: 4,
                      border: "1px solid #ced4da",
                    }}
                  />
                  <div>
                    <p className="mb-1 text-success small">
                      Foto klar i pasfoto-format.
                    </p>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      onClick={() => setCropSource(foto)}
                    >
                      Tilpas igen
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div>
              <label className="form-label" htmlFor="idkort-bemaerkninger">
                Bemærkninger
              </label>
              <textarea
                id="idkort-bemaerkninger"
                className="form-control"
                rows={3}
                value={bemaerkninger}
                onChange={(event) => setBemaerkninger(event.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <div className="d-flex justify-content-end">
          <Button
            id="idkort-submit"
            type="submit"
            className="btn btn-primary"
            disabled={
              isSubmitting || !hasRequiredStamdata || status.formLocked || !foto
            }
          >
            {isSubmitting ? (
              <span className="d-inline-flex align-items-center gap-2">
                <Spinner inline size={16} label="" />
                Sender...
              </span>
            ) : (
              "Send"
            )}
          </Button>
        </div>
      </form>

      <PassportCropModal
        file={cropSource}
        onCancel={() => setCropSource(null)}
        onConfirm={handleCropConfirm}
      />

      {/* Kvitteringen. Pop-up frem for en besked i statuszonen, fordi medlemmet skal
          se den, uden at have opdaget, at der står noget længere oppe (ADV-233). */}
      <ModalRoot
        controller={{ open: kvitteringOpen, setOpen: setKvitteringOpen }}
      >
        <Modal
          title="Bestilling modtaget"
          description="Kvittering for din bestilling af ID-kort"
          footer={
            <div className="d-flex justify-content-end p-4">
              <Button
                id="idkort-kvittering-luk"
                color="primary"
                onClick={() => setKvitteringOpen(false)}
              >
                OK
              </Button>
            </div>
          }
        >
          <p className="mb-0">
            Vi har modtaget din bestilling. {EKSPEDITIONSTID}
          </p>
        </Modal>
      </ModalRoot>

      <ModalRoot
        controller={{
          open: confirmOpen,
          setOpen: (open) => {
            if (!isSubmitting) {
              setConfirmOpen(open);
              if (!open) resetConfirmGate();
            }
          },
        }}
      >
        <Modal
          title="Kontrollér dine oplysninger"
          description="Bekræft at dine registrerede oplysninger er korrekte, før du bestiller"
          footer={
            <div className="d-flex justify-content-end gap-2 p-4">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setConfirmOpen(false);
                  resetConfirmGate();
                }}
                disabled={isSubmitting}
              >
                Annuller
              </button>
              <Button
                id="idkort-submit-confirm"
                color="primary"
                onClick={submitOrder}
                disabled={!canConfirmSend}
              >
                {isSubmitting ? (
                  <span className="d-inline-flex align-items-center gap-2">
                    <Spinner inline size={16} label="" />
                    Sender...
                  </span>
                ) : (
                  "Bekræft og send"
                )}
              </Button>
            </div>
          }
        >
          <div className="d-flex flex-column gap-4">
            <p className="mb-0">
              Dit ID-kort produceres og sendes fysisk til den adresse, som
              Advokatsamfundet har registreret på dig. Kontrollér oplysningerne
              herunder nøje — især leveringsadressen.
            </p>

            <StamdataInfoSection>
              <InfoField label="Fulde navn" value={prefilledValues.fuldeNavn} />
              <InfoField label="Firmanavn" value={prefilledValues.firmanavn} />
              {stamdata?.titel?.navn ? (
                <InfoField label="Titel" value={stamdata.titel.navn} />
              ) : null}
              <InfoField label="E-mail" value={stamdata?.email ?? ""} />
              <InfoField
                label="Telefon"
                value={stamdata?.telefonMobilNr ?? ""}
              />
            </StamdataInfoSection>

            <div
              className={`rounded border p-4 ${
                hasUsableAdresse
                  ? "border-primary-subtle bg-primary-subtle"
                  : "border-danger-subtle bg-danger-subtle"
              }`}
            >
              <p className="form-label mb-2 text-body fw-semibold">
                Leveringsadresse
              </p>
              {hasUsableAdresse ? (
                <address className="mb-0" style={{ fontStyle: "normal" }}>
                  {adresseLinjer.map((linje, i) => (
                    <div key={i}>{linje}</div>
                  ))}
                </address>
              ) : (
                <p className="mb-0 text-danger">
                  Advokatsamfundet har ikke registreret en leveringsadresse på
                  dig.
                </p>
              )}
            </div>

            {/* Ordlyden er kundens egen (ADV-245) — ret den ikke uden at spørge.
                Åbningen skifter med tilstanden: uden en registreret adresse er
                ingenting "forkert" — der mangler noget, og medlemmet kan slet
                ikke bestille endnu. Halen er den samme, for vejen videre er den
                samme formular og den samme frist. */}
            <div className="alert alert-warning mb-0" role="note">
              <p className="mb-1">
                {hasUsableAdresse
                  ? "Står dit navn eller adresse forkert, kan du rette oplysningerne via denne formular:"
                  : "Du kan først bestille et ID-kort, når adressen er registreret. Det sker via denne formular:"}
              </p>
              <p className="mb-1">
                <a
                  className="link"
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.advokatsamfundet.dk/regler-for-advokater/oplysninger-du-skal-give-til-advokatsamfundet/aendringer-i-dine-forhold/benyt-denne-formular-til-din-indberetning/"
                >
                  Nyt job eller ændring i dine forhold
                </a>
              </p>
              <p className="mb-0">
                Bemærk at det kan tage 7 dage før ændringerne er slået igennem.
                Når ændringerne er slået igennem, kan du genoptage bestillingen
                af ID-kortet.
              </p>
            </div>

            <div className="form-check">
              <input
                id="idkort-adresse-bekraeft"
                type="checkbox"
                className="form-check-input"
                checked={adresseBekraeftet}
                disabled={!hasUsableAdresse || isSubmitting}
                onChange={(e) => setAdresseBekraeftet(e.target.checked)}
              />
              <label
                className="form-check-label"
                htmlFor="idkort-adresse-bekraeft"
              >
                Jeg bekræfter, at leveringsadressen ovenfor er korrekt.
              </label>
            </div>
          </div>
        </Modal>
      </ModalRoot>
    </>
  );
};

export default IdKortForm;
